import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { intervalToDuration } from "date-fns";
import { Maximize2, Pause, Play, VolumeOff } from "lucide-react";
import { useRef, useState } from "react";
import {
  ControlsBar,
  ControlsBarContainer, ControlsContainer, LeftControlsContainer,
  BigPlayButton,
  BigPlayButtonContainer,
  ProgressBar, ProgressBarCompletion, ControlsButtonContainer
} from "./AcademyPlayer.styles";
import { AcademyPlayerProps } from "./AcademyPlayer.types";

const AcademyPlayer = ({ url, width = "100%", height = "100%" }: AcademyPlayerProps) => {

  const theme = useTheme();
  const playerRef = useRef<HTMLVideoElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimeout = useRef<number | null>(null);

  const togglePlay = () => {
    const player = playerRef.current;
    if (!player) return;
    if (isPlaying && currentTime < duration) player.pause();
    else player.play();
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    const player = playerRef.current;
    if(!player) return;
    player.muted = !player.muted;
    setIsMuted(!player.muted);
  };

  const toggleFullScreen = () => {
    const player = playerRef.current;
    if (!player) return;
    player.requestFullscreen();
  };

  const onLoadedMeta = () => {
    const player = playerRef.current;
    if (!player) return;
    setDuration(player.duration);
  };

  const onTimeUpdate = () => {
    const player = playerRef.current;
    if (!player) return;
    setCurrentTime(player.currentTime);
  };

  const onEnter = () => {
    if (hideTimeout.current) window.clearTimeout(hideTimeout.current);
    setControlsVisible(true);
  };

  const onLeave = () => {
    if (hideTimeout.current) window.clearTimeout(hideTimeout.current);
    hideTimeout.current = window.setTimeout(() => setControlsVisible(false), 500);
  };

  const completion = (currentTime / duration) * 100;
  const currentTimeInterval = intervalToDuration({ start: 0, end: currentTime * 1000 });
  const durationInterval = intervalToDuration({ start: 0, end: duration * 1000 });

  return (
    <Box
      sx={ {
        width,
        height,
        position: "relative",
        paddingX: "40px"
      } }
      onMouseEnter={ onEnter }
      onMouseLeave={ onLeave }
    >

      <BigPlayButtonContainer theme={ theme } $isPlaying={ isPlaying } $controlsVisible={ controlsVisible }>
        <BigPlayButton theme={ theme } onClick={ togglePlay }>
          { isPlaying && currentTime < duration ? (
            <Pause color={ theme.customColors.textPrimaryControl } />
          ) : (
            <Play color={ theme.customColors.textPrimaryControl }/>
          ) }
        </BigPlayButton>
      </BigPlayButtonContainer>

      <ControlsBarContainer $isMobile={ isMobile } $controlsVisible={ controlsVisible }>
        <ControlsBar $isMobile={ isMobile }>
          <ProgressBar theme={ theme }>
            <ProgressBarCompletion theme={ theme } $value={ completion } />
          </ProgressBar>

          <ControlsContainer>
            <LeftControlsContainer>
              <ControlsButtonContainer onClick={ togglePlay }>
                { isPlaying ? (
                  <Pause size={ 14 } color={ theme.customColors.textPrimaryControl }/>
                ) : (
                  <Play size={ 14 } color={ theme.customColors.textPrimaryControl }/>
                ) }
              </ControlsButtonContainer>
              <ControlsButtonContainer onClick={ toggleMute }>
                { isMuted ? (
                  <VolumeOff size={ 14 } color={ theme.customColors.textPrimaryControl }/>
                ) : (
                  <VolumeOff size={ 14 } color={ theme.customColors.textPrimaryControl }/>
                ) }
              </ControlsButtonContainer>
              <Typography fontSize={ 14 }>{ formatDuration(currentTimeInterval) } / { formatDuration(durationInterval) }</Typography>
            </LeftControlsContainer>
            <ControlsButtonContainer theme={ theme } onClick={ toggleFullScreen }>
              <Maximize2 size={ 14 } color={ theme.customColors.textPrimaryControl }/>
            </ControlsButtonContainer>
          </ControlsContainer>
        </ControlsBar>
      </ControlsBarContainer>

      { /* eslint-disable-next-line jsx-a11y/media-has-caption */ }
      <video
        ref={ playerRef }
        src={ url }
        controls={ false }
        style={ { width: "100%", height: "100%", position: "absolute", top: 0, right: 0, bottom: 0, left: 0 } }
        onLoadedMetadata={ onLoadedMeta }
        onTimeUpdate={ onTimeUpdate }
      />
    </Box>
  );
};

export default AcademyPlayer;

const formatDuration = (duration: Duration) => {
  const h = duration.hours || 0;
  const m = duration.minutes || 0;
  const s = duration.seconds || 0;

  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");

  if (h > 0) {
    const hh = String(h).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }

  return `${mm}:${ss}`;
};
